import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "./constants";

/**
 * The Style Hub, read from the blog API.
 *
 * The website used to read `client/public/content/manifest.json` and
 * `client/public/content/<slug>.json`, compiled from markdown at build time.
 * Both are gone: the posts live in D1 and are served by the Worker at
 * `/api/blog` and `/api/blog/<slug>`, published rows only.
 *
 * ## Nulls become empty
 *
 * The API distinguishes "nobody has said" (`null`) from "the answer is none"
 * (`[]`), which the compiled JSON could not express. Nothing on the website
 * acts on that difference, and every consumer here was written against the
 * compiled shape where absent meant `""` or `[]`. So the distinction is
 * flattened once, here, rather than at each of the dozen places that map over
 * `bodyTypes` or test `featuredImage` for truthiness.
 */

export interface BlogHeading {
  level: number;
  text: string;
  id: string;
}

export interface BlogFaq {
  question: string;
  answer: string;
}

export interface BlogPostSummary {
  slug: string;
  title: string;
  type: string;
  category: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  featuredImage: string;
  shortAnswer: string;
  primaryKeyword: string;
  bodyTypes: string[];
  occasions: string[];
  seasons: string[];
  colourPalettes: string[];
  relatedSlugs: string[];
  readingTime: number;
  publishedDate: string;
}

export interface BlogPost extends BlogPostSummary {
  html: string;
  headings: BlogHeading[];
  faqs: BlogFaq[];
}

/** Quiz intros live at top-level routes (`/body-shape-quiz`), not under
 *  `/style-hub/`, so every Style Hub listing has to leave them out. The
 *  compiled manifests did this by writing to two different files; the API
 *  returns one list, so the split is made here. */
export const QUIZ_INTRO_TYPE = "quiz-intro";

export function isStyleHubPost(post: { type: string }): boolean {
  return post.type !== QUIZ_INTRO_TYPE;
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");
const list = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((entry) => String(entry)) : [];

export function normaliseSummary(raw: Record<string, unknown>): BlogPostSummary {
  return {
    slug: text(raw.slug),
    title: text(raw.title),
    type: text(raw.type),
    category: text(raw.category),
    description: text(raw.description),
    metaTitle: text(raw.metaTitle) || `${text(raw.title)} | Capsule Chic Hub`,
    metaDescription: text(raw.metaDescription) || text(raw.description),
    featuredImage: text(raw.featuredImage),
    shortAnswer: text(raw.shortAnswer),
    primaryKeyword: text(raw.primaryKeyword),
    bodyTypes: list(raw.bodyTypes),
    occasions: list(raw.occasions),
    seasons: list(raw.seasons),
    colourPalettes: list(raw.colourPalettes),
    relatedSlugs: list(raw.relatedSlugs),
    readingTime: typeof raw.readingTime === "number" ? raw.readingTime : 1,
    publishedDate: text(raw.publishedDate),
  };
}

export function normalisePost(raw: Record<string, unknown>): BlogPost {
  return {
    ...normaliseSummary(raw),
    html: text(raw.html),
    headings: Array.isArray(raw.headings) ? (raw.headings as BlogHeading[]) : [],
    faqs: Array.isArray(raw.faqs) ? (raw.faqs as BlogFaq[]) : [],
  };
}

/** The API's own ceiling (`limit` maxes out at 100). The corpus is 33 today, so
 *  one request covers it, and the loop below covers the day it does not. */
const PAGE_SIZE = 100;

/**
 * A listing, and whether it is the whole listing.
 *
 * The build-time twin (`scripts/lib/blog-api.mjs`) throws on a truncated
 * corpus, because half a Style Hub deployed as if it were all of it takes pages
 * out of Google's index for weeks. A browser is a different call: showing a
 * reader 12 of 33 guides beats showing them an error page. But it must not be
 * shown to them AS 33, and it used to be: `total` defaulted to `batch.length`,
 * so a short page redefined "complete" to mean "whatever arrived", and an empty
 * page broke the loop and returned silently.
 *
 * So the partiality travels with the data instead of being discarded, and
 * `StyleHubIndex` says so on the page.
 */
export interface BlogListResult {
  posts: BlogPostSummary[];
  /** What the API says exists. `null` when it did not say. */
  total: number | null;
  /** Every post the API reports having was actually collected. */
  complete: boolean;
}

export async function fetchBlogPosts(): Promise<BlogListResult> {
  const posts: BlogPostSummary[] = [];
  let offset = 0;
  let total: number | null = null;
  let truncated = false;

  do {
    const res = await fetch(`${API_BASE_URL}/api/blog?limit=${PAGE_SIZE}&offset=${offset}`);
    if (!res.ok) throw new Error(`Blog listing failed: ${res.status}`);
    const page = await res.json();
    const batch: Record<string, unknown>[] = Array.isArray(page?.posts) ? page.posts : [];
    // A missing `total` is recorded as missing, never as "the batch is all of
    // it": that substitution is what made a truncated answer indistinguishable
    // from a complete one.
    if (typeof page?.total === "number") total = page.total;
    posts.push(...batch.map(normaliseSummary));
    // A page that comes back empty while the total says otherwise would spin
    // forever. Stop and render what arrived, flagged as incomplete.
    if (batch.length === 0) {
      truncated = total === null || posts.length < total;
      break;
    }
    offset += batch.length;
  } while (total === null || posts.length < total);

  const complete = !truncated && total !== null && posts.length >= total;
  return { posts, total, complete };
}

export async function fetchBlogPost(slug: string): Promise<BlogPost> {
  const res = await fetch(`${API_BASE_URL}/api/blog/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error(`Post not found: ${slug}`);
  return normalisePost(await res.json());
}

/** Newest first, matching the order the compiled manifest was written in and
 *  the order `/api/blog` already returns. Applied again here so a consumer that
 *  slices the first three (the home page rail) does not depend on the server's
 *  default sort staying what it is. */
export function byNewest(a: BlogPostSummary, b: BlogPostSummary): number {
  return (b.publishedDate || "").localeCompare(a.publishedDate || "");
}

export const BLOG_LIST_QUERY_KEY = ["blog", "posts"] as const;

/**
 * Every published Style Hub post, quiz intros excluded, newest first, plus
 * whether that is all of them. A consumer that renders the list without reading
 * `complete` is presenting a possibly partial index as the full one.
 */
export function useStyleHubPosts() {
  return useQuery<BlogListResult>({
    queryKey: BLOG_LIST_QUERY_KEY,
    queryFn: async () => {
      const { posts, total, complete } = await fetchBlogPosts();
      return { posts: posts.filter(isStyleHubPost).sort(byNewest), total, complete };
    },
    staleTime: Infinity,
  });
}

export function useBlogPost(slug: string | undefined) {
  return useQuery<BlogPost>({
    queryKey: ["blog", "post", slug],
    queryFn: () => fetchBlogPost(slug as string),
    staleTime: Infinity,
    enabled: !!slug,
  });
}
